<script setup lang="ts">
import { ref } from 'vue';
import i18n from '@/i18n';
import { useMixins } from '../useMixins';
import { LANG_ENGLISH } from '@/constants';
import { usePromosStore } from '@/store/PromosStore';
import { useToast } from 'primevue/usetoast';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import Button from 'primevue/button';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import Toast from 'primevue/toast';
import TopBannerPreview from './TopBannerPreview.vue';
import BottomBannerPreview from './BottomBannerPreview.vue';
import BottomBannerUpload from './BottomBannerUpload.vue';
import TopBannerUpload from './TopBannerUpload.vue';

const promosStore = usePromosStore();
const toast = useToast();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const isShowError = ref(false);
const previewDataTopBanner = ref(null);
const previewDataBottomBanner = ref(null);
const isTopBannerTab = ref(true);
const topBannerUpload = ref();
const bottomBannerUpload = ref();

const initPayLoadTopBanner = () => {
  return {
    hPageEn: {
      title: '',
      subTitle: '',
      imageTitle: '',
      metaTitle: '',
      metaKeyword: '',
      metaDescription: '',
      image: {
        desktop: '',
        mobile: '',
        tablet: '',
        widescreen: ''
      }
    },
    hPageFr: {
      title: '',
      subTitle: '',
      imageTitle: '',
      metaTitle: '',
      metaKeyword: '',
      metaDescription: '',
      image: {
        desktop: '',
        mobile: '',
        tablet: '',
        widescreen: ''
      }
    }
  };
};

const initPayLoadBottomBanner = () => {
  return {
    hPageEn: {
      imageTitle: '',
      image: {
        desktop: '',
        mobile: '',
        tablet: '',
        widescreen: ''
      },
      urlLink: ''
    },
    hPageFr: {
      imageTitle: '',
      image: {
        desktop: '',
        mobile: '',
        tablet: '',
        widescreen: ''
      },
      urlLink: ''
    }
  };
};

const payloadTopBanner = ref(initPayLoadTopBanner());

const payloadBottomBanner = ref(initPayLoadBottomBanner());

const { currentLanguage: currentLanguageTopBanner } = useMixins(payloadTopBanner.value);
const { currentLanguage: currentLanguageBottomBanner } = useMixins(payloadBottomBanner.value);
const objectCopy = ref();

const handleCancel = async () => {
  if (isTopBannerTab.value) {
    topBannerUpload.value.resetTopMultipleUploadFile();
    getTopBannerHomePageInfo();
  } else {
    bottomBannerUpload.value.resetBottomMultipleUploadFile();
    getBottomBannerPageInfo();
  }
};

//Change type
const switchTopBannerTab = () => {
  isTopBannerTab.value = true;
};

const switchBottomBannerTab = () => {
  isTopBannerTab.value = false;
};
const hasTopBannerData = ref(false);
const getTopBannerHomePageInfo = () => {
  return promosStore
    .getTopBannerHomePageInfo()
    .then((res) => {
      if (res) {
        hasTopBannerData.value = true;
        for (const property in payloadTopBanner.value) {
          if (!res[property].image) {
            payloadTopBanner.value[property] = {
              ...res[property],
              image: payloadTopBanner.value[property].image
            };
          } else {
            payloadTopBanner.value[property] = res[property];
          }
        }
        objectCopy.value = { ...payloadTopBanner.value };
        handlePreviewTopBanner(currentLanguageTopBanner === LANG_ENGLISH ? res.hPageEn : res.hPageFr);
      }
    })
    .catch(() => {
      hasTopBannerData.value = false;
    });
};
const hasBottomBannerData = ref(false);
const getBottomBannerPageInfo = () => {
  return promosStore
    .getBottomBannerHomePageInfo()
    .then((res) => {
      if (res) {
        hasBottomBannerData.value = true;
        for (const property in payloadBottomBanner.value) {
          if (!res[property].image) {
            payloadBottomBanner.value[property] = {
              ...res[property],
              image: payloadBottomBanner.value[property].image
            };
          } else {
            payloadBottomBanner.value[property] = res[property];
          }
        }

        objectCopy.value = { ...payloadBottomBanner.value };
        handlePreviewBottomBanner(currentLanguageBottomBanner === LANG_ENGLISH ? res.hPageEn : res.hPageFr);
      }
    })
    .catch(() => {
      hasBottomBannerData.value = false;
    });
};

const handlePreviewTopBanner = (payloadPreview) => {
  previewDataTopBanner.value = { ...payloadPreview };
};

const handlePreviewBottomBanner = (payloadPreview) => {
  previewDataBottomBanner.value = { ...payloadPreview };
};

const handleSubmit = async () => {
  if (isTopBannerTab.value) {
    promosStore.submitTopBanner(payloadTopBanner.value).then(async () => {
      toast.add({ severity: 'success', summary: '', detail: i18n.global.t('notification.save_successfully'), life: 3000 });
      isShowError.value = false;
      topBannerUpload.value.resetTopMultipleUploadFile();
      getTopBannerHomePageInfo();
    });
  } else {
    promosStore.submitBottomBanner(payloadBottomBanner.value).then(async () => {
      toast.add({ severity: 'success', summary: '', detail: i18n.global.t('notification.save_successfully'), life: 3000 });
      isShowError.value = false;
      bottomBannerUpload.value.resetBottomMultipleUploadFile();
      getBottomBannerPageInfo();
    });
  }
};
const handleDelete = async () => {
  if (isTopBannerTab.value) {
    promosStore.submitTopBanner(initPayLoadTopBanner()).then(async () => {
      toast.add({ severity: 'success', summary: '', detail: i18n.global.t('notification.delete_successfully'), life: 3000 });
      isShowError.value = false;
      topBannerUpload.value.resetTopMultipleUploadFile();
      getTopBannerHomePageInfo();
    });
  } else {
    promosStore.submitBottomBanner(initPayLoadBottomBanner()).then(async () => {
      toast.add({ severity: 'success', summary: '', detail: i18n.global.t('notification.delete_successfully'), life: 3000 });
      isShowError.value = false;
      bottomBannerUpload.value.resetBottomMultipleUploadFile();
      getBottomBannerPageInfo();
    });
  }
};
getTopBannerHomePageInfo();
getBottomBannerPageInfo();

const handleRemovePreviewImgBanner = (key, lang: string, payload) => {
  if (lang === LANG_ENGLISH) {
    payload.hPageEn.image[key] = '';
    return;
  }
  payload.hPageFr.image[key] = '';
};
</script>

<template>
  <div class="home-promo">
    <Toast position="top-left" group="tl" />
    <SplitterPanel class="ml-2 mb-3 hide-gutter">
      <Button :label="$t('promos.labels.top_section')" id="top-section" :class="['mt-3', 'mr-2', isTopBannerTab ? 'opacity-100' : 'opacity-60']" @click="switchTopBannerTab()" />
      <Button :label="$t('promos.labels.bottom_section')" id="bottom-section" :class="['mt-3', 'mr-2', isTopBannerTab ? 'opacity-60' : 'opacity-100']" @click="switchBottomBannerTab()" />
    </SplitterPanel>
    <Splitter layout="vertical">
      <SplitterPanel class="mt-5 px-3">
        <TopBannerPreview :value="previewDataTopBanner" v-if="isTopBannerTab && hasTopBannerData"></TopBannerPreview>
        <BottomBannerPreview ref="" :value="previewDataBottomBanner" v-if="!isTopBannerTab && hasBottomBannerData"></BottomBannerPreview>
      </SplitterPanel>
      <SplitterPanel class="mt-5 px-3">
        <TopBannerUpload
          ref="topBannerUpload"
          v-if="isTopBannerTab"
          @preview="handlePreviewTopBanner"
          v-model:payload="payloadTopBanner"
          @handleRemovePreviewImgBanner="(key, lang) => handleRemovePreviewImgBanner(key, lang, payloadTopBanner)"
          :previewDataTopBanner="previewDataTopBanner"
        ></TopBannerUpload>
        <BottomBannerUpload
          ref="bottomBannerUpload"
          v-if="!isTopBannerTab"
          @preview="handlePreviewBottomBanner"
          v-model:payload="payloadBottomBanner"
          @handleRemovePreviewImgBanner="(key, lang) => handleRemovePreviewImgBanner(key, lang, payloadBottomBanner)"
          :previewDataBottomBanner="previewDataBottomBanner"
        ></BottomBannerUpload>
      </SplitterPanel>

      <SplitterPanel class="p-3 text-center justify-content-center" v-if="authorizations?.configurationPage?.promos_edit">
        <Button :label="$t('promos.save')" icon="pi pi-check" id="btn-save" class="p-button-secondary mr-2" @click="handleSubmit()" />
        <Button :label="$t('promos.delete')" icon="pi pi-trash" id="btn-delete" class="mr-2" @click="handleDelete()" />
        <Button :label="$t('promos.labels.cancel')" icon="pi pi-times" id="btn-cancel" @click="handleCancel()" />
      </SplitterPanel>
    </Splitter>
  </div>
</template>
